/**
 * Standard-Base64-Decoder (mit Padding-Toleranz) ohne `atob`.
 *
 * Die Konvention verbietet `atob`/`btoa`; daher manuelles Alphabet. Standard-
 * Base64 (`+/` als Sonderzeichen, kein URL-safe) ist das Format der
 * Tenant-Public-Keys aus `config.json` und damit konsistent mit der
 * Backend-Seite (`Buffer.from(s, 'base64')`).
 *
 * URL-safe Base64 (`-_`, ohne Padding) gehört in den TokenCodec (DEV-005);
 * darf hier explizit nicht akzeptiert werden, weil Tenant-Keys nicht in
 * URLs liegen.
 */

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const BITS_PER_BYTE = 8;
const BITS_PER_CHAR = 6;
const BYTE_MASK = 0xff;
const GROUP_OUTPUT_CHARS = 4;
const GROUP_INPUT_BYTES = 3;

const CHAR_TO_VALUE: ReadonlyMap<string, number> = buildLookup();

function buildLookup(): ReadonlyMap<string, number> {
  const map = new Map<string, number>();
  for (let index = 0; index < BASE64_ALPHABET.length; index++) {
    const char = BASE64_ALPHABET[index];
    if (char !== undefined) {
      map.set(char, index);
    }
  }
  return map;
}

/**
 * Dekodiert Standard-Base64 (mit oder ohne `=`-Padding) in Bytes.
 *
 * @param input Standard-Base64-String.
 * @throws {Error} mit Message `INVALID_BASE64`, wenn der Input ungültig ist
 *   (unzulässige Zeichen, unmögliche Längen-Restklasse, frühe Pad-Zeichen).
 */
export function base64ToBytes(input: string): Uint8Array {
  const stripped = input.replace(/=+$/u, '');
  if (stripped.length === 0) {
    return new Uint8Array(0);
  }
  ensureValidLength(stripped.length);
  const output = new Uint8Array(decodedLength(stripped.length));
  let writeIndex = 0;
  for (let readIndex = 0; readIndex < stripped.length; readIndex += GROUP_OUTPUT_CHARS) {
    const chunkLength = Math.min(GROUP_OUTPUT_CHARS, stripped.length - readIndex);
    writeIndex += decodeChunk(stripped, readIndex, chunkLength, output, writeIndex);
  }
  return output;
}

function ensureValidLength(strippedLength: number): void {
  if (strippedLength % GROUP_OUTPUT_CHARS === 1) {
    throw new Error('INVALID_BASE64');
  }
}

function decodedLength(strippedLength: number): number {
  const remainder = strippedLength % GROUP_OUTPUT_CHARS;
  const fullGroups = Math.floor(strippedLength / GROUP_OUTPUT_CHARS);
  const tailBytes = remainder === 0 ? 0 : remainder - 1;
  return fullGroups * GROUP_INPUT_BYTES + tailBytes;
}

function decodeChunk(
  input: string,
  readIndex: number,
  chunkLength: number,
  output: Uint8Array,
  writeIndex: number,
): number {
  const sextet0 = lookup(input[readIndex]);
  const sextet1 = lookup(input[readIndex + 1]);
  const sextet2 = chunkLength > 2 ? lookup(input[readIndex + 2]) : 0;
  const sextet3 = chunkLength > 3 ? lookup(input[readIndex + 3]) : 0;
  const combined =
    (sextet0 << (BITS_PER_CHAR * 3)) |
    (sextet1 << (BITS_PER_CHAR * 2)) |
    (sextet2 << BITS_PER_CHAR) |
    sextet3;
  output[writeIndex] = (combined >> (BITS_PER_BYTE * 2)) & BYTE_MASK;
  if (chunkLength > 2) {
    output[writeIndex + 1] = (combined >> BITS_PER_BYTE) & BYTE_MASK;
  }
  if (chunkLength > 3) {
    output[writeIndex + 2] = combined & BYTE_MASK;
  }
  return chunkLength - 1;
}

function lookup(char: string | undefined): number {
  if (char === undefined) {
    throw new Error('INVALID_BASE64');
  }
  const value = CHAR_TO_VALUE.get(char);
  if (value === undefined) {
    throw new Error('INVALID_BASE64');
  }
  return value;
}
